import { AgentClient } from 'agents/client'
import { hash, isEqual } from 'ohash'
import { useIDBKeyval } from '@vueuse/integrations/useIDBKeyval'
import { useAuthToken } from '~/queries/auth/token'
import type { AgentConnectionState } from '~/types/agents'

type AgentOptions = {
  agent: MaybeRefOrGetter<string>
  name: MaybeRefOrGetter<string>
  host: string
  baseURL?: string
}

export function useAgent<T extends Record<string, unknown>>({
  agent,
  name,
  host,
  baseURL,
}: AgentOptions) {
  const AGENT = toValue(agent)
  const NAME = toValue(name)

  if (!AGENT || !NAME || !host) {
    throw new Error('Agent, name, and host are required')
  }

  const {
    data: state,
    set: setLocalState,
    isFinished: stateReady,
  } = useIDBKeyval<T>(`@rialtic/agents/${AGENT}/${NAME}`, {} as T, {
    deep: true,
    shallow: false,
  })

  const serverStateLoaded = shallowRef(false)
  const connectionState = ref<AgentConnectionState>('disconnected')
  const connectionError = ref<string | null>(null)

  const stateUpdatedHook = createEventHook<{
    state: T
    source: 'client' | 'server'
    user?: any
  }>()

  const toPlain = <U>(value: U): U => {
    const raw = toRaw(value)

    try {
      return structuredClone(raw)
    } catch {
      return JSON.parse(
        JSON.stringify(raw, (_key, val) =>
          typeof val === 'function' || typeof val === 'symbol'
            ? undefined
            : val,
        ),
      ) as U
    }
  }

  const persistState = (agentState: T) => {
    if (isEqual(agentState, state.value)) {
      return Promise.resolve()
    }
    return setLocalState(toPlain(agentState))
  }

  const {
    data: authToken,
    refetch: refetchAuthToken,
    status,
  } = useAuthToken()
  const connection = new AgentClient({
    agent: AGENT,
    name: NAME,
    host,
    query: async () => {
      if (authToken.value && status.value === 'success') {
        return { token: authToken.value }
      }
      connectionState.value = 'connecting'
      connectionError.value = null
      try {
        // Force-and-await a real fetch (refetch, not refresh — refresh can
        // resolve to a still-pending state and drop the token). Read from the
        // resolved data or the reactive ref, and never connect without a token.
        const result = await refetchAuthToken()
        const token = result?.data ?? authToken.value
        if (!token) {
          throw new Error('No auth token available for agent connection')
        }
        return { token }
      } catch (error) {
        connectionState.value = 'error'
        connectionError.value =
          error instanceof Error ? error.message : 'Failed to connect to agent'
        throw error
      }
    },
    onStateUpdate: async (agentState: T, source) => {
      stateUpdatedHook.trigger({
        state: agentState,
        source,
      })

      if (!serverStateLoaded.value) {
        if (source === 'server') {
          await persistState(agentState)
          serverStateLoaded.value = true
          return
        }
        // client update before server state initialized
        // return early to avoid persisting stale state
        return
      }

      await persistState(agentState)
    },
  })

  // Track connection state changes
  const updateConnectionState = () => {
    if (connection.CLOSED) {
      connectionState.value = 'disconnected'
    } else {
      connectionState.value = 'connected'
    }
  }

  // Listen for connection events
  connection.addEventListener('open', () => {
    connectionState.value = 'connected'
    connectionError.value = null
  })

  connection.addEventListener('close', () => {
    connectionState.value = 'disconnected'
  })

  connection.addEventListener('error', (event) => {
    connectionState.value = 'error'
    connectionError.value =
      event instanceof Error ? event.message : 'Connection error'
  })

  const { $datadog } = useNuxtApp()
  const messageHook = createEventHook<{
    type: string
    data?: any
    user?: any
  }>()
  connection.addEventListener('message', (event) => {
    if (typeof event.data === 'string') {
      try {
        const data = JSON.parse(event.data)
        messageHook.trigger(data)
      } catch (error) {
        $datadog?.addError(error)
      }
    }
  })

  const reconnectInterval = ref<NodeJS.Timeout>()

  onBeforeMount(() => {
    if (connection.CLOSED) {
      connectionState.value = 'connecting'
      connection.reconnect()
    } else {
      updateConnectionState()

      reconnectInterval.value = setInterval(
        () => {
          if (
            Boolean(connectionError.value) ||
            connectionState.value === 'disconnected'
          ) {
            connection.reconnect()
          }
        },
        1000 * 60 * 5,
      )
    }
  })
  onBeforeUnmount(() => {
    clearInterval(reconnectInterval.value)
    connection.close()
  })

  const { user } = useUserSession()
  const sendMessage = async (message: { type: string; data: any }) => {
    if (!message.type || !message.data) {
      return
    }
    connection.send(
      JSON.stringify({
        ...message,
        user: { ...user.value, id: hash(user.value?.sub) },
      }),
    )
  }

  const agentFetch = $fetch.create({
    baseURL: `${baseURL}/${AGENT}/${NAME}`,
  })

  const setState = (state: T) => {
    connection.setState(state)
  }

  const updateState = (updates: Partial<T>) => {
    const currentState = toPlain(state.value)
    const nextUpdates = toPlain(updates)

    setState({ ...(currentState as T), ...(nextUpdates as Partial<T>) })
  }

  return {
    agentFetch,
    onMessage: messageHook.on,
    onStateUpdate: stateUpdatedHook.on,
    serverStateLoaded,
    sendMessage,
    state,
    setState,
    stateReady,
    updateState,
    reconnect: () => connection.reconnect(),
    connectionState: readonly(connectionState),
    connectionError: readonly(connectionError),
  }
}
